import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CaseWithRelations {
  id: string;
  status: string | null;
  priority: string | null;
  created_at: string | null;
  google_drive_url: string | null;
  legacy_stage: string | null;
  notes: string | null;
  visa_type: string | null;
  // Computed fields for UI
  case_type_name: string | null;
  created_by_display_name: string | null;
  contact_id: string | null;
  deal_id: string | null;
  deal_name: string | null;
  deal_status: string | null;
  is_cancelled: boolean;
}

export function useCases() {
  const queryClient = useQueryClient();

  const { data: cases = [], isLoading, error, refetch } = useQuery({
    queryKey: ['cases'],
    queryFn: async () => {
      // Fetch cases with nested relations
      const { data, error } = await supabase
        .from('cases')
        .select(`
          id,
          status,
          priority,
          created_at,
          google_drive_url,
          legacy_stage,
          notes,
          visa_type,
          item:items!inner(
            id,
            catalog:catalog(id, name),
            deal:deals!inner(
              id,
              name,
              deal_status,
              cancelled_at,
              cancellation_reason,
              contact:contacts!fk_deals_contact(id, first_name, last_name)
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to flat structure for UI
      return (data || []).map((caseItem: any): CaseWithRelations => {
        const item = caseItem.item;
        const deal = item?.deal;
        const contact = deal?.contact;
        const catalog = item?.catalog;

        const contactName = contact 
          ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() 
          : null;

        return {
          id: caseItem.id,
          status: caseItem.status,
          priority: caseItem.priority,
          created_at: caseItem.created_at,
          google_drive_url: caseItem.google_drive_url,
          legacy_stage: caseItem.legacy_stage,
          notes: caseItem.notes,
          visa_type: caseItem.visa_type,
          // Computed fields
          case_type_name: catalog?.name || null,
          created_by_display_name: contactName || 'Unknown',
          contact_id: contact?.id || null,
          deal_id: deal?.id || null,
          deal_name: deal?.name || null,
          deal_status: deal?.deal_status || null,
          is_cancelled: deal?.deal_status === 'cancelled',
        };
      });
    },
  });

  const createCaseMutation = useMutation({
    mutationFn: async (caseData: any) => {
      const { data, error } = await supabase
        .from('cases')
        .insert(caseData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { data, error } = await supabase
        .from('cases')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });

  const deleteCaseMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cases')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });

  return {
    cases,
    isLoading,
    error,
    refetch,
    createCase: createCaseMutation.mutateAsync,
    updateCase: updateCaseMutation.mutateAsync,
    deleteCase: deleteCaseMutation.mutateAsync,
    isCreating: createCaseMutation.isPending,
    isUpdating: updateCaseMutation.isPending,
    isDeleting: deleteCaseMutation.isPending,
  };
}

export function useCase(id: string | null) {
  const queryClient = useQueryClient();

  const { data: caseData, isLoading, error, refetch } = useQuery({
    queryKey: ['case', id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('cases')
        .select(`
          *,
          case_contacts!case_contacts_case_id_fk(
            *,
            contact:contacts!case_contacts_contact_fk(id, first_name, last_name, primary_email, primary_phone, date_of_birth, country_of_birth, primary_address, primary_phone_country_code, metadata)
          ),
          assignments:case_assignments!case_assignments_case_id_fk(
            *,
            user:app_users!case_assignments_user_id_fk(
              id,
              email,
              first_name,
              last_name,
              display_name
            )
          ),
          item:items(
            id,
            num_dependents,
            catalog:catalog(id, name, primary_person_label, secondary_person_label, dependent_label, enabled_roles, allows_dependents, role_labels),
            deal:deals(id, stage, deal_status, cancelled_at, cancellation_reason, total_amount, contact_id, sales_consultant, sold_by:app_users!sales_consultant(id, first_name, last_name, display_name))
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const updateCaseMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { data, error } = await supabase
        .from('cases')
        .update(updates)
        .eq('id', id!)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
    },
  });

  return { caseData, isLoading, error, refetch, updateCase: updateCaseMutation.mutateAsync };
}
